import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'
import { withMask } from 'use-mask-input'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Button, InputField } from '@/components/ui'
import { useDadosCompradorPJ } from '@/features/proposta/hooks/use-dados-comprador-pj'
import { usePessoaJuridicaPorCnpj } from '@/features/proposta/hooks/use-pessoa-juridica'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/layouts'
import type {
  CompradorPessoaJuridicaSchemaInput,
  CompradorPessoaJuridicaSchemaOutput,
} from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import { PhoneNumberInputField } from '@/shared/phone-number'
import { Cnpj } from '@/utils/cnpj'
import { CNPJ_MASK } from '@/utils/masks'

type TDadosComprador = {
  onBack: () => void
  onNext: () => void
  nacionalidadePrincipalId?: string
  compradores: TCompradoresCadastrados
  onFinish: (value: CompradorPessoaJuridicaSchemaOutput) => Promise<void>
  isLast: boolean
  emailsJaVinculados: Record<string, string>
}

export const DadosCompradorPJ = ({
  onBack,
  onNext,
  nacionalidadePrincipalId,
  compradores,
  isLast,
  onFinish,
  emailsJaVinculados,
}: TDadosComprador) => {
  const {
    formState: { errors, isSubmitting },
    setValue,
    watch,
    control,
    handleSubmit,
  } = useFormContext<CompradorPessoaJuridicaSchemaInput, unknown, CompradorPessoaJuridicaSchemaOutput>()
  const cnpj = watch('dados.cnpj')
  const cnpjRaw = new Cnpj(cnpj ?? '').removeFormatting()
  const isEditing = watch('mode') === 'edit'

  const buscouCpf = watch('dados.buscouCnpj')
  const prefix = 'proposta:cadastro_compradores.steps.dados_comprador_pj'
  const { refetch, isFetching: isFetchingPessoaJuridica } = usePessoaJuridicaPorCnpj(cnpjRaw, false)
  const { handlers } = useDadosCompradorPJ({
    nacionalidadePrincipalId: nacionalidadePrincipalId,
    onNext,
    buscarPorCNPJ: refetch,
    compradores,
    emailsJaVinculados,
  })

  const handleNext = async () => {
    if (isLast) {
      handleSubmit(onFinish)()
      return
    }
    await handlers.validateOnNext()
  }

  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>{t(`${prefix}.titulo`)}</StepPropostaHeaderTitle>
      </StepPropostaHeaderContent>
      <StepPropostaContent hasBorder>
        <div className="h-[calc(100%-105px)] w-full">
          <h3 className="font-poppins neutral-800 mb-6 tracking-wide">{t(`${prefix}.subtitulo`)}</h3>
          <div className="mt-1 mb-4 grid max-w-114 grid-cols-[182px_1fr] items-end gap-4 md:grid-cols-[1fr_127px] lg:grid-cols-[362px_1fr] lg:gap-10">
            <InputField
              ref={withMask(CNPJ_MASK.mask, CNPJ_MASK)}
              id="cpf"
              name="dados.cnpj"
              control={control}
              type="text"
              label={t(`${prefix}.label.cnpj`)}
              placeholder={t(`${prefix}.placeholder.cnpj`)}
              disabled={buscouCpf || isEditing}
              afterContent={<Icon name="search" className="text-muted-foreground" />}
            />
            <Render.If condition={!isEditing}>
              {() => (
                <Render.If
                  condition={buscouCpf}
                  fallback={
                    <Button
                      isLoading={isFetchingPessoaJuridica}
                      variant="outline"
                      showLoadingText={false}
                      className="mb-7 border-gray-300 bg-white hover:bg-gray-50 sm:mb-9"
                      disabled={!cnpjRaw || !!errors.dados?.cnpj}
                      onClick={handlers.handleBuscarConta}
                    >
                      {t(`${prefix}.buscar`)}
                    </Button>
                  }
                >
                  {() => (
                    <Button
                      isLoading={isFetchingPessoaJuridica}
                      showLoadingText={false}
                      variant="ghost"
                      className="text-destructive hover:text-destructive mb-7 border-gray-300 bg-white sm:mb-9"
                      disabled={!!errors.dados?.cnpj}
                      onClick={handlers.handleLimpar}
                    >
                      <span className="sm:hidden">{t(`${prefix}.limpar_mobile`)}</span>
                      <span className="hidden sm:inline">{t(`${prefix}.limpar`)}</span>
                    </Button>
                  )}
                </Render.If>
              )}
            </Render.If>
          </div>
          <div className="mb-6 border-t border-neutral-200" />
          <div className="relative flex flex-1 flex-col gap-2 md:grid md:grid-cols-1 md:gap-0">
            <InputField
              className="max-w-90"
              disabled={!buscouCpf}
              required
              control={control}
              name="dados.razaoSocial"
              label={t(`${prefix}.label.razao_social`)}
              placeholder={t(`${prefix}.placeholder.razao_social`)}
              maxLength={100}
            />
            <PhoneNumberInputField
              disabled={!buscouCpf}
              className="sm:w-90"
              control={control}
              setValue={setValue}
              nationalityName="dados.telefone.origem"
              phoneName="dados.telefone.numero"
              label={t(`${prefix}.label.telefone`)}
            />
            <InputField
              disabled={!buscouCpf}
              className="max-w-90"
              name="dados.email"
              control={control}
              label={t(`${prefix}.label.email`)}
              placeholder={t(`${prefix}.placeholder.email`)}
            />
          </div>
        </div>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button variant="outline" onClick={onBack} className="font-nunito h-12 w-full md:w-auto">
          {t('proposta:cadastro_compradores.steps.voltar')}
        </Button>
        <Button
          className="font-nunito h-12 w-full md:w-27"
          disabled={isSubmitting}
          isLoading={isSubmitting}
          showLoadingText={false}
          onClick={handleNext}
        >
          {t(isLast ? 'proposta:cadastro_compradores.steps.concluir' : 'proposta:cadastro_compradores.steps.proximo')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
